import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Ban, CreditCard, LogIn, Mail, MoreHorizontal, PlayCircle, Trash2, Users } from "lucide-react";
import { NOTICE_LABEL } from "@/components/platform/BillingNoticesDrawer";

const STATUS = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-rose-50 text-rose-700 border-rose-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  none: "bg-slate-50 text-slate-600 border-slate-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  past_due: "bg-orange-50 text-orange-700 border-orange-200",
};
const LABEL = { paused: "Suspended", none: "No subscription", pending: "Never subscribed", past_due: "Past due" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function AgencyRow({ agency: a, plans, isSelf, selected, onSelect, onAction, onDelete, onNotices, onUsers }) {
  const notice = a.last_billing_notice;
  return (
    <tr className={`border-t border-slate-100 ${selected ? "bg-rose-50/40" : "hover:bg-slate-50/60"}`} data-testid={`platform-agency-${a.id}`}>
      <td className="px-3 py-2"><input type="checkbox" checked={selected} disabled={isSelf} onChange={onSelect} data-testid={`platform-select-${a.id}`} /></td>
      <td className="px-3 py-2">
        <div className="font-medium text-slate-900 flex items-center gap-2">
          {a.name}
          {isSelf && <Badge variant="outline" className="text-[10px]">you</Badge>}
          {a.is_test && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">test</Badge>}
        </div>
        <div className="text-xs text-slate-500">{a.code ? `code ${a.code}` : ""}{a.email ? ` · ${a.email}` : ""}</div>
      </td>
      <td className="px-3 py-2 text-slate-700 capitalize">{a.plan && a.plan !== "none" ? a.plan : "—"}{a.mrr ? <span className="text-xs text-slate-400"> · ${a.mrr}/mo</span> : null}</td>
      <td className="px-3 py-2">
        <Badge variant="outline" className={STATUS[a.status] || STATUS.none} data-testid={`platform-status-${a.id}`}>{LABEL[a.status] || a.status}</Badge>
        {notice && (
          <button type="button" onClick={onNotices} className="block mt-1 text-[11px] text-slate-500 hover:text-teal-700 hover:underline text-left" title="Billing notice history" data-testid={`platform-notice-${a.id}`}>
            <Mail className="w-3 h-3 inline mr-1" />{(NOTICE_LABEL[notice.kind] || { label: notice.kind }).label} · {new Date(notice.at).toLocaleDateString()}{a.billing_notice_count > 1 ? ` (+${a.billing_notice_count - 1})` : ""}
          </button>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600">{a.users} users · {a.employees} staff · {a.clients} clients</td>
      <td className="px-3 py-2 text-xs text-slate-600">{fmt(a.last_login)}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{fmt(a.created_at)}</td>
      <td className="px-3 py-2 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`platform-actions-${a.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => onAction("impersonate")} data-testid={`platform-impersonate-${a.id}`}><LogIn className="w-4 h-4 mr-2" /> Log in as agency admin</DropdownMenuItem>
            <DropdownMenuItem onClick={onUsers} data-testid={`platform-users-${a.id}`}><Users className="w-4 h-4 mr-2" /> Users &amp; roles</DropdownMenuItem>
            <DropdownMenuItem onClick={onNotices} data-testid={`platform-notices-${a.id}`}><Mail className="w-4 h-4 mr-2" /> Billing notices{a.billing_notice_count ? ` (${a.billing_notice_count})` : ""}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {a.status === "paused" ? (
              <DropdownMenuItem onClick={() => onAction("restore")} data-testid={`platform-restore-${a.id}`}><PlayCircle className="w-4 h-4 mr-2" /> Restore access</DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled={isSelf} onClick={() => onAction("suspend")} data-testid={`platform-suspend-${a.id}`}><Ban className="w-4 h-4 mr-2" /> Suspend access</DropdownMenuItem>
            )}
            {a.stripe_subscription_id && (
              <DropdownMenuItem onClick={() => onAction("cancel-stripe")} data-testid={`platform-cancel-stripe-${a.id}`}><CreditCard className="w-4 h-4 mr-2" /> Cancel in Stripe</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-slate-500">Set plan</DropdownMenuLabel>
            {Object.entries(plans).map(([id, price]) => (
              <DropdownMenuItem key={id} disabled={a.plan === id && a.status === "active"} onClick={() => onAction("plan", { plan_id: id })} className="capitalize" data-testid={`platform-plan-${a.id}-${id}`}>
                {id} <span className="ml-auto text-xs text-slate-400">${price}/mo</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={isSelf} onClick={onDelete} className="text-rose-600 focus:text-rose-700" data-testid={`platform-delete-${a.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete agency…</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
